import React, { useContext } from 'react';
import { Card, Spin } from '@bb/elements-web';
import styled from 'styled-components';
import { AuthContext } from 'contexts';
import { useAnsweringServiceDetails } from 'hooks';
import { Feature } from '@bb/automaton';
import useOrgBillingModel from 'hooks/useOrgBillingModel';
import CurrentServicesSection from './components/CurrentServicesSection';
import AvailableServicesSection from './components/AvailableServicesSection';
import { ViewingLevel } from '../..';

export interface AllSubscriptionsCardProps {
  /** ID of the user viewing this page */
  userId: string;
  /** Whether these are the subscriptions for an indivdual user or for a brokerage paying for all its users */
  viewingLevel: ViewingLevel;
  /** Callback function when the Org Plan (i.e. Tier) button is clicked */
  onOrgPlanClick: () => void;
  /** Callback function when the Answering Service button is clicked */
  onAnsweringServiceClick: () => void;
  /** Callback function when the Supra Subscription button is clicked */
  onSupraOneClick: () => void;
}

/**
 * Main subscriptions card displaying a user or brokerage's current services
 * (i.e. active subscriptions) and available services they can sign up for.
 */
const AllSubscriptionsCard: React.FC<AllSubscriptionsCardProps> = ({
  userId,
  viewingLevel,
  onOrgPlanClick,
  onAnsweringServiceClick,
  onSupraOneClick,
}) => {
  // #region States & variables

  const { hasFeature } = useContext(AuthContext);

  /** Whether the SUPRA_MVP flag is turned on granting access to all Supra One features for this org. */
  const canUseSupraOne = hasFeature(Feature.SUPRA_MVP);

  const {
    answeringServiceType,
    answeringServicePayer,
    hasActiveAnsweringService,
    fetchErrorAnsweringService,
    loadingAnsweringData,
  } = useAnsweringServiceDetails({ viewingLevel, userId });

  const {
    supraSubscriptionPayer,
    getOrgBillingModelError,
    loadingOrgBillingModel,
  } = useOrgBillingModel();

  return (
    <Card dataCy="all-subscriptions-card">
      <ContentContainer>
        {loadingAnsweringData || loadingOrgBillingModel ? (
          <SpinnerWrapper>
            <Spin dataCy="all-subscriptions-card-loading" />
          </SpinnerWrapper>
        ) : (
          <>
            <CurrentServicesSection
              answeringServicePayer={answeringServicePayer}
              answeringServiceType={answeringServiceType}
              canUseSupraOne={canUseSupraOne}
              fetchErrorAnsweringService={fetchErrorAnsweringService}
              fetchErrorSupraOne={getOrgBillingModelError}
              hasActiveAnsweringService={hasActiveAnsweringService}
              onAnsweringServiceClick={onAnsweringServiceClick}
              onOrgPlanClick={onOrgPlanClick}
              onSupraOneClick={onSupraOneClick}
              supraSubscriptionPayer={supraSubscriptionPayer}
              viewingLevel={viewingLevel}
            />
            <AvailableServicesSection
              answeringServicePayer={answeringServicePayer}
              answeringServiceType={answeringServiceType}
              canUseSupraOne={canUseSupraOne}
              fetchErrorAnsweringService={fetchErrorAnsweringService}
              hasActiveAnsweringService={hasActiveAnsweringService}
              onAnsweringServiceClick={onAnsweringServiceClick}
              viewingLevel={viewingLevel}
            />
          </>
        )}
      </ContentContainer>
    </Card>
  );
};

// #endregion JSX to return

export default AllSubscriptionsCard;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;
